import type { Router } from 'vue-router'

/**
 * Holds the app's router instance behind a leaf module (no view imports), so
 * low-level utils can use the router WITHOUT statically importing `@/router`.
 * Importing `@/router` from a util pulls the entire route/view graph into a
 * circular dependency, which breaks HMR (TDZ errors → full page reload).
 *
 * `@/router` registers the instance here via `setRouterInstance` at creation.
 */
let router: Router | null = null

export function setRouterInstance (newRouterInstance: Router): void {
  router = newRouterInstance
}

export function getRouter (): Router {
  if (!router) {
    throw new Error('Router instance not set yet')
  }
  return router
}

/**
 * Check if the router is ready and has already completed its initial navigation.
 *
 * Since `router.isReady()` returns a promise, this is able to guess without waiting for a promise.
 * Especially useful in scenarios where we're working _in the router_ and can't wait for it to be ready.
 */
export const isRouterReady = (): boolean =>
  getRouter().currentRoute.value.matched.length !== 0

/**
 * Get the current route even if the router is not ready yet.
 */
export const getCurrentRouteSafe = () => {
  const router = getRouter()
  return isRouterReady()
    ? router.currentRoute.value
    : router.resolve(router.options.history.location)
}
